import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { selectGameRulesActive } from "../../../../State/selectors";
import { selectRtp } from "../../../../State/Slices/gameSettingsSlice";
import { PixiSettings } from "../../../../pixiSettings";
import { usePrimaryUIToolbarDimensionsV2 } from "../../../../Hooks/usePrimaryUIToolbarDimensionsV2";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { getUITextureAsDataURL } from "../../../../assets";

const MAX_WIN_MULTIPLIER = 5000;

// Theme colors (matching backport UI)
const ACCENT_COLOR = "#ffbf11";
const TRACK_COLOR = "rgba(13, 22, 84, 0.8)";
const ICON_SIZE_EM = 1.3;

// Animation timing (matching WarningPopup)
const OVERLAY_FADE_DURATION = 200; // ms — wait for menu overlay to fade in
const CONTENT_ANIM_DURATION = 250; // ms — content fade in/out

const GameRulesSection = () => {
  const gameRulesActive = useSelector(selectGameRulesActive);
  const { dimensions, device, orientation, uiSymbolScale, getResponsiveConfig } =
    useDimensions();
  const toolbar = usePrimaryUIToolbarDimensionsV2();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Get RTP value from Redux (sourced from API init, falls back to window.__OPTIONS__)
  const rtpValue = useSelector(selectRtp) || "";

  const [htmlContent, setHtmlContent] = useState("");
  const [isVisible, setIsVisible] = useState(false);       // controls CSS opacity/transform
  const [shouldRender, setShouldRender] = useState(false);  // controls DOM presence
  const scrollRef = useRef(null);
  const prevActiveRef = useRef(false);

  // Top toolbar height — matches MenuToolbarTop: buttonConfig.height * heightMultiplier (1.0)
  const topToolbarHeight = useMemo(() => {
    return buttonConfig.height * 1.0;
  }, [buttonConfig.height]);

  // Bottom toolbar Y — matches MenuToolbarBottom positioning exactly
  const bottomToolbarY = useMemo(() => {
    return dimensions.height - toolbar.height;
  }, [dimensions.height, toolbar.height]);

  // Scroll area = space between top toolbar bottom edge and bottom toolbar top edge
  const scrollArea = useMemo(() => ({
    top: topToolbarHeight,
    height: bottomToolbarY - topToolbarHeight,
    width: dimensions.width,
  }), [topToolbarHeight, bottomToolbarY, dimensions.width]);

  // Responsive font sizes (matching reference screenshot proportions)
  const fontSizes = useMemo(() => {
    let multiplier = 1.0;
    if (device === PixiSettings.DEVICE.MOBILE) {
      multiplier = orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.8 : 1.2;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multiplier = orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.5 : 1.5;
    }
    const base = 28 * multiplier * uiSymbolScale;
    return {
      title: Math.floor(base * 1.5),
      subtitle: Math.floor(base * 1.2),
      body: Math.floor(base),
    };
  }, [device, orientation, uiSymbolScale]);

  // Content max width
  const contentMaxWidth = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? dimensions.width - 32
        : toolbar.width * 0.5;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? dimensions.width * 0.65
        : toolbar.width * 0.55;
    }
    return toolbar.width * 0.45;
  }, [device, orientation, dimensions.width, toolbar.width]);

  // Replace {{ICON:xxx}} placeholders with <img> tags
  const processIcons = useCallback(
    (html) => {
      const iconSize = Math.floor(fontSizes.body * ICON_SIZE_EM);
      return html.replace(/\{\{ICON:(\w+)\}\}/g, (_, iconName) => {
        const dataURL = getUITextureAsDataURL(iconName);
        return `<img src="${dataURL}" class="rules-inline-icon" style="width:${iconSize}px;height:${iconSize}px;" alt="${iconName}" />`;
      });
    },
    [fontSizes.body],
  );

  // Wait for PIXI assets to be fully loaded before processing spritesheet icons
  const assetsLoaded = useSelector((state) => state.assets.status === "succeeded");

  // Load and cache raw HTML content once on mount (don't process icons yet — spritesheet may not be loaded)
  const rawHtmlRef = useRef(null);

  useEffect(() => {
    const loadRulesHTML = async () => {
      try {
        const basePath =
          window.__OPTIONS__?.base_path || window.__BASE_PATH__ || "";
        const htmlPath = `${basePath}/rules/backport/rules.html`;
        const response = await fetch(htmlPath);
        if (!response.ok) return;

        let html = await response.text();
        html = html.replace(/{MAX_MULTIPLIER}/g, MAX_WIN_MULTIPLIER.toLocaleString());
        html = html.replace(/{RTP_VALUE}/g, rtpValue.toString());
        rawHtmlRef.current = html;
      } catch (error) {
        // Fail silently
      }
    };

    loadRulesHTML();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process icons only after assets are fully loaded (spritesheet in PIXI cache)
  // Also re-processes on resize since processIcons depends on fontSizes
  useEffect(() => {
    if (rawHtmlRef.current && assetsLoaded) {
      setHtmlContent(processIcons(rawHtmlRef.current));
    }
  }, [processIcons, assetsLoaded]);

  // Fade-in / fade-out logic (matching WarningPopup pattern)
  useEffect(() => {
    let fadeInTimer;
    let fadeOutTimer;

    if (gameRulesActive && !prevActiveRef.current) {
      // Opening: mount DOM immediately, then trigger fade-in after overlay delay
      setShouldRender(true);
      setIsVisible(false);
      fadeInTimer = setTimeout(() => {
        setIsVisible(true);
        // Reset scroll position after fade starts
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
      }, OVERLAY_FADE_DURATION);
    } else if (!gameRulesActive && prevActiveRef.current) {
      // Closing: fade out content first, then unmount
      setIsVisible(false);
      fadeOutTimer = setTimeout(() => {
        setShouldRender(false);
      }, CONTENT_ANIM_DURATION);
    }

    prevActiveRef.current = gameRulesActive;

    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(fadeOutTimer);
    };
  }, [gameRulesActive]);

  if (!shouldRender) return null;

  return (
    <>
      {/* Scroll area — full width, text centered via padding, scrollbar at screen edge */}
      <div
        ref={scrollRef}
        className="backport-rules-scroll"
        style={{
          position: "fixed",
          top: `${scrollArea.top}px`,
          left: 0,
          width: `${dimensions.width}px`,
          height: `${scrollArea.height}px`,
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: `${fontSizes.body}px`,
          paddingBottom: `${fontSizes.body}px`,
          paddingLeft: `${(dimensions.width - contentMaxWidth) / 2}px`,
          paddingRight: `${(dimensions.width - contentMaxWidth) / 2}px`,
          boxSizing: "border-box",
          zIndex: 9500,
          pointerEvents: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${ACCENT_COLOR} ${TRACK_COLOR}`,
          // Fade animation
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: `opacity ${CONTENT_ANIM_DURATION}ms ease-out, transform ${CONTENT_ANIM_DURATION}ms ease-out`,
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {/* Scoped styles */}
      <style>{`
        .backport-rules-scroll h2 {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${Math.floor(fontSizes.title * 1.3)}px;
          font-weight: 800;
          color: ${ACCENT_COLOR};
          text-align: center;
          margin: ${fontSizes.body * 0.5}px 0 ${fontSizes.body * 2}px 0;
          line-height: ${Math.floor(fontSizes.title * 1.3 * 1.3)}px;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
        }

        .backport-rules-scroll h3 {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.title}px;
          font-weight: bold;
          color: #ffffff;
          text-align: center;
          margin: ${fontSizes.body * 2}px 0 ${fontSizes.body * 1.2}px 0;
          line-height: ${fontSizes.title * 1.3}px;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
        }

        .backport-rules-scroll h4 {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.subtitle}px;
          font-weight: bold;
          color: #ffffff;
          text-align: center;
          margin: ${fontSizes.body * 1.2}px 0 ${fontSizes.body * 0.6}px 0;
          line-height: ${fontSizes.subtitle * 1.3}px;
          text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
        }

        .backport-rules-scroll p {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.body}px;
          font-weight: 600;
          color: #ffffff;
          text-align: center;
          margin: 0 0 ${fontSizes.body * 0.7}px 0;
          line-height: ${fontSizes.body * 1.5}px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }

        .backport-rules-scroll strong {
          font-weight: 700;
        }

        .backport-rules-scroll section {
          margin-bottom: ${fontSizes.body * 0.5}px;
        }

        .backport-rules-scroll .rules-inline-icon {
          display: inline-block;
          vertical-align: middle;
          margin: 0 2px;
        }

        /* Webkit scrollbar */
        .backport-rules-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .backport-rules-scroll::-webkit-scrollbar-track {
          background: ${TRACK_COLOR};
        }
        .backport-rules-scroll::-webkit-scrollbar-thumb {
          background: ${ACCENT_COLOR};
        }
        .backport-rules-scroll::-webkit-scrollbar-thumb:hover {
          background: #ffd044;
        }
      `}</style>
    </>
  );
};

export default GameRulesSection;
